import React, { useEffect, useState } from "react";
import { Button, Form, Input, DatePicker, Row, Col, Select } from "antd";
import { post } from "../../../../services/services";
import { ApiURL } from "../../../../services/apiConstants";
import dayjs from "dayjs";
import { ErrorToast, SuccessToast } from "../../../../widgets/AppToast";

const RefrigerationEquipLogForm = ({
  mode = "add",
  odsId: propOdsId,
  recordId,
  records = [],
  readOnly = false,
  isSaved = false,
  onSaveSuccess,
  isStandalone = false,
}) => {
  const [form] = Form.useForm();
  const [equipmentList, setEquipmentList] = useState([]);
  const [selectedOdsId, setSelectedOdsId] = useState(propOdsId);

  const designation = JSON.parse(localStorage.getItem("user_data"))?.data
    ?.designation;

  const isElectricalEngineer = designation === "Electrical Engineer";
  const isChiefEngineer = designation === "Chief Engineer";

  const canEditEngineerSign = isElectricalEngineer && !readOnly;
  const canEditChiefSign = isChiefEngineer && !readOnly;

  useEffect(() => {
    if (isStandalone && mode === "add") {
      const userData = JSON.parse(localStorage.getItem("user_data"));
      const shipId = userData?.data?.ship_details?.ship_id;
      const fd = new FormData();
      fd.append("ship_id", shipId);

      post(ApiURL.list_ods_record, fd).then((res) => {
        if (res?.status) {
          setEquipmentList(res.data);
        }
      });
    }
  }, [isStandalone, mode]);

  /* ---------------- PREFILL ---------------- */
  useEffect(() => {
    if (records.length > 0) {
      const latest = records[records.length - 1];

      form.setFieldsValue({
        date: latest?.date ? dayjs(latest.date) : null,
        refrigerant_added_kgs: latest.refrigerant_added_kgs,
        initial_system_charge: latest.initial_system_charge,
        refrigerant_recovered: latest.refrigerant_recovered,
        leak_test_results: latest.leak_test_results,
        auto_leak_detection_test: latest.auto_leak_detection_test,
        remarks_followup: latest.remarks_followup,
        original_oil_type_filtering_ltrs:
          latest.original_oil_type_filtering_ltrs,
        system_oil_added_changed_ltrs: latest.system_oil_added_changed_ltrs,
        sign_engineer: latest.sign_engineer,
        sign_chief_engineer: latest.sign_chief_engineer,
      });
    }
  }, [records, form]);

  const appendIfFilled = (fd, key, value) => {
    if (value !== undefined && value !== "" && value !== null) {
      fd.append(key, value);
    }
  };

  const onFinish = async (values) => {


    if (!values?.date) {
      ErrorToast("Error", "Date is required");
      return;
    }

    // 🔐 Role-based signature check
    if (!values.sign_engineer) {
      ErrorToast("Error", "operation incharge signature is required");
      return;
    }

    if (canEditChiefSign && !values.sign_chief_engineer) {
      ErrorToast("Error", "Chief Engineer signature is required");
      return;
    }

    const fd = new FormData();

    fd.append("date", dayjs(values.date).format("YYYY-MM-DD"));
    appendIfFilled(fd, "refrigerant_added_kgs", values.refrigerant_added_kgs);
    appendIfFilled(fd, "initial_system_charge", values.initial_system_charge);
    appendIfFilled(fd, "refrigerant_recovered", values.refrigerant_recovered);
    appendIfFilled(fd, "leak_test_results", values.leak_test_results);
    appendIfFilled(
      fd,
      "auto_leak_detection_test",
      values.auto_leak_detection_test,
    );
    appendIfFilled(fd, "remarks_followup", values.remarks_followup);
    appendIfFilled(
      fd,
      "original_oil_type_filtering_ltrs",
      values.original_oil_type_filtering_ltrs,
    );
    appendIfFilled(
      fd,
      "system_oil_added_changed_ltrs",
      values.system_oil_added_changed_ltrs,
    );
    fd.append("sign_engineer", values.sign_engineer);


    if (canEditChiefSign) {
      fd.append("sign_chief_engineer", values.sign_chief_engineer);
    }

    const api = mode === "edit" ? ApiURL.ods_record_update : ApiURL.ods_record;

    if (mode === "edit") {
      fd.append("id", recordId);
    } else {
      fd.append("id", selectedOdsId || propOdsId);
    }

    const res = await post(api, fd);

    if (res?.status === true) {
      SuccessToast("Success", "ODS record saved successfully");

      // Notification API Call
      try {
        const notifFd = new FormData();
        notifFd.append("record_id", res?.data?.id || res?.id || selectedOdsId || propOdsId || "");
        notifFd.append("type", "refrigeration");
        notifFd.append("description", `New Refrigeration Equipment Log added`);

        await post(ApiURL.add_refrigeration_ods_notification, notifFd);
      } catch (err) {
        console.error("Failed to send notification:", err);
      }

      // Notify parent that form is saved
      if (typeof onSaveSuccess === "function") {
        onSaveSuccess();
      }
    } else {
      const responseData = res?.response?.data || res;
      if (responseData?.errors) {
        form.setFields(
          Object.keys(responseData.errors).map((field) => ({
            name: field,
            errors: Array.isArray(responseData.errors[field])
              ? responseData.errors[field]
              : [responseData.errors[field]],
          })),
        );
      } else if (responseData?.error && typeof responseData.error === "string") {
        const fieldMatch = responseData.error.match(/Field '(.*?)'/);
        if (fieldMatch && fieldMatch[1]) {
          form.setFields([
            { name: fieldMatch[1], errors: [responseData.error] },
          ]);
        }
      }
      ErrorToast("Error", responseData?.message || responseData?.error || "Failed to save ODS record");
    }
  };

  // 🔹 LOAD SAVED ODS RECORD (ADD MODE)
  useEffect(() => {
    if (!propOdsId || mode !== "add") return;

    const fd = new FormData();
    fd.append("id", propOdsId);

    post(ApiURL.ods_record_get, fd).then((res) => {
      if (res?.status && res.data) {
        form.setFieldsValue({
          ...res.data,
          date: res.data.date ? dayjs(res.data.date) : null,
        });
      }
    });
  }, [propOdsId, mode]);

  return (
    <div className="white-container">
      <h3 className="section-title">ODS Record Details</h3>

      <Form 
        layout="vertical" 
        form={form} 
        onFinish={onFinish}
        onValuesChange={(changedValues) => {
          const fieldName = Object.keys(changedValues)[0];
          form.setFields([{ name: fieldName, errors: [] }]);
        }}
      >
        {records.length > 0 && records[0]?.equipment && (
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item label="Equipment">
                <Input value={records[0]?.equipment} disabled={readOnly} />
              </Form.Item>
            </Col>
          </Row>
        )}
        {isStandalone && mode === "add" && (
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label="Select Equipment"
                name="ods_id"
                rules={[{ required: true, message: "Please select equipment" }]}
              >
                <Select
                disabled={readOnly}
                  placeholder="Select Equipment to add record"
                  onChange={(val) => setSelectedOdsId(val)}
                >
                  {equipmentList.map((item) => (
                    <Select.Option key={item.ods_id} value={item.ods_id}>
                      {item.equipment} ({item.location_on_board})
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        )}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Date" name="date" rules={[{ required: true }]}>
              <DatePicker style={{ width: "100%" }}  disabled={readOnly}/>
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label="Refrigerant Added (kgs)"
              name="refrigerant_added_kgs"
              rules={[
                {
                  pattern: /^[0-9]+(\.[0-9]+)?$/,
                  message: "Please enter a valid number",
                },
              ]}
            >
              <Input  disabled={readOnly}/>
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label="Initial System Charge"
              name="initial_system_charge"
              rules={[
                {
                  pattern: /^[0-9]+(\.[0-9]+)?$/,
                  message: "Please enter a valid number",
                },
              ]}
            >
              <Input  disabled={readOnly}/>
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label="Refrigerant Recovered"
              name="refrigerant_recovered"
              rules={[
                {
                  pattern: /^[0-9]+(\.[0-9]+)?$/,
                  message: "Please enter a valid number",
                },
              ]}
            >
              <Input  disabled={readOnly}/>
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item label="Leak Test Results" name="leak_test_results">
              <Input  disabled={readOnly}/>
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label="Auto Leak Detection Test"
              name="auto_leak_detection_test"
            >
              <Input  disabled={readOnly}/>
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item label="Remarks / Follow-up" name="remarks_followup">
              <Input  disabled={readOnly}/>
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label="Original Oil Type Filtering (Ltrs)"
              name="original_oil_type_filtering_ltrs"
              rules={[
                {
                  pattern: /^[0-9]+(\.[0-9]+)?$/,
                  message: "Please enter a valid number",
                },
              ]}
            >
              <Input  disabled={readOnly}/>
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label="System Oil Added / Changed (Ltrs)"
              name="system_oil_added_changed_ltrs"
              rules={[
                {
                  pattern: /^[0-9]+(\.[0-9]+)?$/,
                  message: "Please enter a valid number",
                },
              ]}
            >
              <Input  disabled={readOnly}/>
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label="Signed by operation incharge"
              name="sign_engineer"
             
            >
              <Input disabled={readOnly} />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label="Signed by Chief Engineer"
              name="sign_chief_engineer"
              rules={
                canEditChiefSign
                  ? [
                      {
                        required: true,
                        message: "Chief Engineer signature required",
                      },
                    ]
                  : []
              }
              extra={!canEditChiefSign ? "Only Chief Engineer can sign" : ""}
            >
              <Input disabled={!canEditChiefSign} />
            </Form.Item>
          </Col>
        </Row>

        <Button type="primary" htmlType="submit" disabled={readOnly}>
            Save ODS Record
          </Button>
      </Form>
    </div>
  );
};

export default RefrigerationEquipLogForm;
